import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { MouseEvent, ReactElement } from 'react';
import * as ReactDOM from 'react-dom';

import { Bem } from '../../../service/Bem/Bem';
import {
    getRelativeToViewportPosition,
    RelativeToViewportPosition
} from '../../../service/Dom/Dom';
import { getVisibleTextItemsCount } from '../../../service/FilterHelpers/FilterHelpers';
import { SuggestItem } from '../../../typings/filter';
import { ButtonDecorator } from '../../ButtonDecorator/ButtonDecorator';
import { ContainerOverflow } from '../../ContainerOverflow/ContainerOverflow';
import { Shape2, Shape2Icon } from '../../Shape2/Shape2';

import './FilterSuggest.css';
import * as keyset from './FilterSuggest.i18n';

const i18n = i18nFactory(keyset);

export const visibleTextItemsCount = 3;

export enum FilterSuggestControlFocus {
    ACCEPT = 'ACCEPT',
    BACK = 'BACK'
}

export type FilterSuggestControlFocusValue = FilterSuggestControlFocus | number;

export interface FilterSuggestProps {
    title: string;
    hasPrevStep?: boolean;
    hasAccept?: boolean;
    onPrevClick?: () => void;
    onAcceptClick?: () => void;
    textItems: SuggestItem[];
    items: SuggestItem[];
    focus?: FilterSuggestControlFocusValue; // какой элемент сейчас выбран: либо номер пункта саджеста, либо одна из кнопок
    onRequestFocusChange?: (value?: FilterSuggestControlFocusValue) => void;
    isAcceptButtonDisabled?: boolean;
}

export class FilterSuggest extends React.Component<FilterSuggestProps> {
    private itemsContainer: React.ReactInstance | null;

    componentDidUpdate() {
        let selectedChild = this.props.items.find(
            child => child.props.isSelected === true
        );

        if (!selectedChild && this.props.items && this.props.items.length > 0) {
            selectedChild = this.props.items[0];
        }

        if (selectedChild && this.itemsContainer) {
            const componentNode = this.itemsContainer as Element;
            const selectedNode = ReactDOM.findDOMNode(
                this.refs[`item-${selectedChild.props.id}`]
            ) as Element;
            const relativePosition = getRelativeToViewportPosition(
                selectedNode,
                componentNode
            );

            if (relativePosition !== RelativeToViewportPosition.inside) {
                selectedNode.scrollIntoView(
                    relativePosition === RelativeToViewportPosition.higher
                );
            }
        }
    }

    render() {
        const { title, items, textItems } = this.props;

        let itemsElem, textItemsElem;
        if (items.length !== 0) {
            itemsElem = (
                <div>
                    <div className={Bem.cls('filter-suggest', 'title')}>
                        {title}
                    </div>
                    <div
                        className={Bem.cls('filter-suggest', 'items')}
                        ref={ref => (this.itemsContainer = ref)}
                    >
                        <ContainerOverflow>
                            {items.map(child =>
                                React.cloneElement(
                                    child as ReactElement<{ ref: string }>,
                                    {
                                        ref: `item-${child.props.id}`
                                    }
                                )
                            )}
                        </ContainerOverflow>
                    </div>
                    {this.getActionsPanel()}
                </div>
            );
        }

        if (textItems.length !== 0) {
            let textItemsRestElem;

            // если все текстовые элементы уже показаны, то не нужно показывать сколько элементов осталось
            if (textItems.length > visibleTextItemsCount) {
                textItemsRestElem = (
                    <div className={Bem.cls('filter-suggest', 'rest')}>
                        {i18n('rest', {
                            count: textItems.length - visibleTextItemsCount
                        })}
                    </div>
                );
            }
            textItemsElem = (
                <div
                    className={Bem.cls('filter-suggest', 'items')}
                    ref={ref => (this.itemsContainer = ref)}
                >
                    {textItems
                        .slice(0, getVisibleTextItemsCount(textItems.length))
                        .map(child =>
                            React.cloneElement(
                                child as ReactElement<{ ref: string }>,
                                {
                                    ref: `item-${child.props.id}`
                                }
                            )
                        )}
                    {textItemsRestElem}
                </div>
            );
        }

        return (
            <div className={Bem.cls('filter-suggest')}>
                {textItemsElem}
                {itemsElem}
            </div>
        );
    }

    private getActionsPanel() {
        const prevStepContent = this.getPrevStepContent();
        const acceptContent = this.getAcceptContent();

        if (!prevStepContent && !acceptContent) {
            return '';
        }

        return (
            <div
                onMouseDown={this.onActionsPanelMouseDown}
                className={Bem.cls('filter-suggest', 'actions-panel')}
            >
                {prevStepContent}
                {acceptContent}
            </div>
        );
    }

    private onActionsPanelMouseDown(e: MouseEvent<HTMLDivElement>) {
        // отменяем дефолтное поведение, чтобы не отработал blur в input
        e.preventDefault();
    }

    private requestChangeFocus = (
        value?: FilterSuggestControlFocusValue
    ) => () => {
        const { onRequestFocusChange } = this.props;
        if (onRequestFocusChange) {
            onRequestFocusChange(value);
        }
    };

    private getPrevStepContent() {
        const { hasPrevStep, onPrevClick, focus } = this.props;
        if (!hasPrevStep || !onPrevClick) {
            return '';
        }
        // TODO DIRECT-73319: разобраться, почему hovered не перерисовывается и убрать случайный key
        return (
            <ButtonDecorator
                key={Math.random()}
                theme={'clear'}
                size={'n'}
                width={'max'}
                hovered={focus === FilterSuggestControlFocus.BACK}
                cls={Bem.cls('filter-suggest', 'prev-step')}
                onMouseEnter={this.requestChangeFocus(
                    FilterSuggestControlFocus.BACK
                )}
                onMouseLeave={this.requestChangeFocus(undefined)}
                attrs={{
                    onClick: onPrevClick
                }}
            >
                <Shape2
                    cls={Bem.cls('filter-suggest', 'prev-step-icon')}
                    icon={Shape2Icon.verticalArrow}
                    width={10}
                    height={10}
                />{' '}
                {i18n('back')}
            </ButtonDecorator>
        );
    }

    private getAcceptContent() {
        const {
            hasAccept,
            onAcceptClick,
            focus,
            onRequestFocusChange,
            isAcceptButtonDisabled
        } = this.props;
        const requestChangeFocus = (
            value?: FilterSuggestControlFocusValue
        ) => () => {
            if (onRequestFocusChange) {
                onRequestFocusChange(value);
            }
        };

        if (!hasAccept || !onAcceptClick) {
            return '';
        }

        // TODO DIRECT-73319: убрать случайный key и поменять hovered на focused после https://st.yandex-team.ru/ISL-4579
        return (
            <ButtonDecorator
                key={Math.random()}
                theme={'action'}
                size={'n'}
                hovered={focus === FilterSuggestControlFocus.ACCEPT}
                pin={'brick-brick'}
                width={'max'}
                onClick={onAcceptClick}
                cls={Bem.cls('filter-suggest', 'accept')}
                disabled={isAcceptButtonDisabled}
                onMouseEnter={requestChangeFocus(
                    FilterSuggestControlFocus.ACCEPT
                )}
                onMouseLeave={requestChangeFocus(undefined)}
            >
                {i18n('accept')}
            </ButtonDecorator>
        );
    }
}
